import React from 'react';
import { AuditAction } from '../../types/audit';
import { EntityIcon, Pill, actionTone, entityKindOf } from '../ui';
import type { SemanticTone } from '../ui';

export const actionLabels: Record<AuditAction, string> = {
  [AuditAction.CREATE]: 'Created',
  [AuditAction.READ]: 'Viewed',
  [AuditAction.UPDATE]: 'Updated',
  [AuditAction.DELETE]: 'Deleted',
  [AuditAction.LOGIN]: 'Logged In',
  [AuditAction.LOGOUT]: 'Logged Out',
  [AuditAction.APPROVE]: 'Approved',
  [AuditAction.REJECT]: 'Rejected',
  [AuditAction.SEARCH]: 'Searched',
};

/** Create green, update blue, delete red, sign-in neutral; reads and searches stay quiet outlines */
export const auditActionTone = (action: AuditAction | string): SemanticTone =>
  action === AuditAction.READ || action === AuditAction.SEARCH ? 'outline' : actionTone(action);

/** Solid dot colour per action tone, for the entity timeline */
export const ACTION_DOT: Record<SemanticTone, string> = {
  green: 'bg-emerald-500',
  blue: 'bg-sky-500',
  red: 'bg-primary-600',
  violet: 'bg-violet-500',
  amber: 'bg-amber-500',
  teal: 'bg-teal-500',
  neutral: 'bg-neutral-300',
  outline: 'bg-neutral-300',
};

export const ENTITY_TYPES: Array<{ value: string; label: string }> = [
  { value: 'stakeholder', label: 'Stakeholder' },
  { value: 'organization', label: 'Organization' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'wiki_page', label: 'Wiki Page' },
  { value: 'media_asset', label: 'Media Asset' },
  { value: 'user', label: 'User' },
];

export const entityTypeLabel = (type?: string) =>
  ENTITY_TYPES.find(t => t.value === type)?.label ??
  (type ? type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ') : '');

export const ActionPill: React.FC<{ action: AuditAction }> = ({ action }) => (
  <Pill tone={auditActionTone(action)}>{actionLabels[action] ?? action}</Pill>
);

/** Entity type with its kind icon (people sky, organizations teal, …); plain text for kinds without a hue */
export const EntityTypeLabel: React.FC<{ type: string; className?: string }> = ({ type, className = '' }) => {
  const kind = entityKindOf(type);
  return (
    <span className={`inline-flex min-w-0 items-center gap-2 ${className}`}>
      {kind && <EntityIcon kind={kind} size="xs" />}
      <span className="truncate">{entityTypeLabel(type)}</span>
    </span>
  );
};

/** "Sep 16, 14:03" */
export const formatShortDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** First block of a UUID, enough to tell rows apart */
export const shortId = (id?: string) => (id && id.length > 12 ? id.slice(0, 8) : id ?? '');

const renderValue = (value: unknown) =>
  value === null || value === undefined || value === ''
    ? '—'
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);

const META_KEYS = ['path', 'method', 'success', 'duration', 'description', 'requestBody', 'responseData', 'changes'];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** Key / value lines; `tone="added"` tints them as written values */
const KeyValues: React.FC<{ data: Record<string, unknown>; tone?: 'added' | 'plain' }> = ({ data, tone = 'plain' }) => {
  const entries = Object.entries(data).filter(([, value]) => value !== undefined);
  if (!entries.length) return null;
  const added = tone === 'added';
  return (
    <div className={`rounded-lg ${added ? 'bg-emerald-50 text-emerald-800' : 'bg-white text-neutral-700 ring-1 ring-inset ring-neutral-200/70'} px-2.5 py-1.5`}>
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-[minmax(0,6.5rem)_1fr] gap-x-3 sm:grid-cols-[minmax(0,10rem)_1fr]">
          <span className={`truncate ${added ? 'text-emerald-600/80' : 'text-neutral-400'}`}>
            {added && (
              <span className="mr-1.5 select-none" aria-hidden="true">
                +
              </span>
            )}
            {key}
          </span>
          <span className="min-w-0 break-words">{renderValue(value)}</span>
        </div>
      ))}
    </div>
  );
};

/** Monospace details: request summary, `changes` as a from/to diff, written fields, then the rest. */
export const AuditDetails: React.FC<{ details: any; className?: string }> = ({ details, className = '' }) => {
  if (details === null || details === undefined || details === '') return null;

  if (!isPlainObject(details)) {
    return (
      <p className={`break-words font-ui-mono text-[12px] text-neutral-600 ${className}`}>
        {typeof details === 'string' ? details : JSON.stringify(details)}
      </p>
    );
  }

  const { path, method, success, duration, description, requestBody, responseData, changes } = details as any;
  const rest = Object.fromEntries(Object.entries(details).filter(([key]) => !META_KEYS.includes(key)));
  const hasRequestLine = !!(method || path || description);

  return (
    <div className={`min-w-0 space-y-1.5 font-ui-mono text-[12px] leading-5 ${className}`}>
      {hasRequestLine && (
        <p className="flex flex-wrap items-baseline gap-x-2 text-neutral-500">
          {description && <span className="font-ui text-[12px] text-neutral-600">{description}</span>}
          {(method || path) && (
            <span className="break-all text-neutral-400">
              {method} {path}
            </span>
          )}
          {typeof duration === 'number' && <span className="tabular-nums text-neutral-400">{duration} ms</span>}
          {success === false && <span className="text-primary-700">failed</span>}
        </p>
      )}

      {isPlainObject(changes) && (
        <div className="space-y-1">
          {Object.entries(changes).map(([field, change]: [string, any]) => (
            <div key={field} className="min-w-0">
              <p className="text-neutral-400">{field}</p>
              {change?.from !== undefined && change?.from !== null && change?.from !== '' && (
                <p className="break-words rounded-md bg-primary-50 px-2 py-0.5 text-primary-700">
                  <span className="mr-1.5 select-none text-primary-400" aria-hidden="true">
                    −
                  </span>
                  <span className="line-through decoration-primary-300">{renderValue(change.from)}</span>
                </p>
              )}
              {change?.to !== undefined && change?.to !== null && change?.to !== '' && (
                <p className="mt-0.5 break-words rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-800">
                  <span className="mr-1.5 select-none text-emerald-500" aria-hidden="true">
                    +
                  </span>
                  {renderValue(change.to)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {isPlainObject(requestBody) && <KeyValues data={requestBody} tone="added" />}
      {Object.keys(rest).length > 0 && <KeyValues data={rest} />}

      {responseData !== undefined && responseData !== null && (
        <details className="group">
          <summary className="cursor-pointer select-none list-none font-ui text-[12px] text-neutral-400 hover:text-ink [&::-webkit-details-marker]:hidden">
            <span className="group-open:hidden">Show response</span>
            <span className="hidden group-open:inline">Hide response</span>
          </summary>
          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white px-2.5 py-1.5 text-neutral-600 ring-1 ring-inset ring-neutral-200/70">
            {JSON.stringify(responseData, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};
